import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { WrongOtpError } from '@root/features/auth'
import { loginModel } from '@root/features/auth/authenticate'
import { backClickedAction } from '@root/features/initialization/atoms/common'
import {
  AuthButton,
  AuthModal,
  AuthModalHeading,
} from '@root/shared/ui/auth-modal'
import { LockerIcon, ProfileIcon } from '@root/shared/ui-kit'
import { OtpInputInline } from '@root/shared/ui-kit/otp-input'

import type { LoginPayload } from '@root/features/auth/authenticate'

type Values = {
  otp: string
}

type Props = {
  loginPayload: LoginPayload
}

export const AuthOtp: React.FC<Props> = ({ loginPayload }) => {
  const { t } = useTranslation()
  const [internalError, setInternalError] = useState<boolean>(false)
  const [requestState] = useAtom(loginModel.loginRequestState)

  const login = useAction(loginModel.loginAction)
  const handleBack = useAction(backClickedAction)

  const { control, formState, handleSubmit, setError } = useForm<Values>({
    defaultValues: {
      otp: '',
    },
  })

  useEffect(() => {
    if (!AsyncStateN.isError(requestState)) {
      return
    }
    if (requestState.error instanceof WrongOtpError) {
      setInternalError(true)
      setTimeout(() => setInternalError(false), 1000)

      setError('otp', { message: t('auth.invalid-otp') })
      return
    }
  }, [requestState, setError, t])

  const handleSubmitInternal = handleSubmit((values) => {
    const otp = values.otp || undefined
    const payload: LoginPayload = {
      ...loginPayload,
      otp,
    }

    login(payload)
  })

  return (
    <AuthModal infoIcon={ProfileIcon} infoMessage={t('auth.info.wallets')}>
      <Root onSubmit={handleSubmitInternal}>
        <AuthModalHeading
          mobileInfoMessage={t('auth.info.wallets')}
          title={t('auth.enter-otp-title')}
          description={t('auth.enter-otp-description')}
          icon={LockerIcon}
          onBack={handleBack}
        />
        <Controller
          control={control}
          name="otp"
          rules={{ required: true, maxLength: 6, minLength: 6 }}
          render={({ field }) => (
            <OtpInputInline
              {...field}
              disabled={requestState.loading}
              hasError={internalError}
            />
          )}
        />
        <AuthButton disabled={!formState.isValid} busy={requestState.loading}>
          {t('auth.continue')}
        </AuthButton>
      </Root>
    </AuthModal>
  )
}

const Root = styled.form`
  display: grid;
  height: 100%;
  align-items: center;

  gap: 16px;
  align-content: start;
  grid-template-rows: auto 1fr auto;
`
